import type { ConceptScope } from "@/types/concept";
import { type Band, gradable, inYearRange, mergeNetworks, paperKey, type PreviewPaper, type SeedNetwork, spreadPick } from "./preview";

export type { Band };
/** Quick-check results for one question: paper key → band, or null when the
 *  model skipped the paper (attempted; never re-sent). */
export type Bands = Record<string, Band | null>;

/** Papers checked per seed (spec §3) and per request (backend max). */
export const TRIAGE_PER_SEED = 60;
export const TRIAGE_CHUNK = 60;

export interface TriagePaper { key: string; title: string; abstract: string | null; year: number | null }

const hashInt = (s: string) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 33) ^ s.charCodeAt(i)) >>> 0;
  return h;
};

/** Results are cached per question: editing it (③, or back at ①) re-checks. */
export const questionHash = (q: string) => hashInt(q.trim()).toString(36);

/** The papers a seed's lane shows and the quick check grades: in range,
 *  gradable (year + abstract), up to TRIAGE_PER_SEED spread across links and years. */
export function lanePick(net: SeedNetwork, scope: ConceptScope | null | undefined): PreviewPaper[] {
  const pool = [...mergeNetworks([net]).values()].filter((x) => inYearRange(x.paper.year, scope) && gradable(x));
  return pool.length <= TRIAGE_PER_SEED ? pool : spreadPick(pool, TRIAGE_PER_SEED);
}

/** ③ samples by ②'s bands only when every seed's lane was checked: bands for
 *  just the first seeds (Next clicked before the check finished) would draw
 *  the whole sample from their papers. */
export function bandsCover(picks: PreviewPaper[][], bands: Bands | undefined): bands is Bands {
  return !!bands && picks.every((pick) => pick.every((x) => paperKey(x.paper) in bands));
}

/** Unchecked papers across seeds, first seed first (callers pass ticked seeds first). */
export function papersToCheck(picks: PreviewPaper[][], done: Bands): TriagePaper[] {
  const out: TriagePaper[] = [];
  const seen = new Set<string>();
  for (const pick of picks) {
    for (const { paper } of pick) {
      const key = paperKey(paper);
      if (seen.has(key) || key in done) continue;
      seen.add(key);
      out.push({ key, title: paper.title.slice(0, 1000), abstract: paper.abstract?.slice(0, 2000) ?? null, year: paper.year ?? null });
    }
  }
  return out;
}

export function chunk<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}

export interface LaneCounts { checked: number; on: number; partly: number; share: number; onEstimate: number }

/** One seed: bands over its checked papers; `onEstimate` scales the on-question
 *  share to `reach` (the "about N papers" the row shows). */
export function laneCounts(pick: PreviewPaper[], bands: Bands, reach: number): LaneCounts {
  const got = pick.map((x) => bands[paperKey(x.paper)]).filter((b): b is Band => b != null);
  const on = got.filter((b) => b === 3).length;
  const partly = got.filter((b) => b === 2).length;
  const share = got.length ? on / got.length : 0;
  return { checked: got.length, on, partly, share, onEstimate: Math.round(share * reach) };
}

export interface LaneSummaryData { total: number; checked: number; on: number; partly: number; off: number }

/** Ticked seeds together: bands over the union of their checked papers,
 *  scaled to `total` (the union's in-range size). Null until any are checked. */
export function laneSummary(tickedPicks: PreviewPaper[][], bands: Bands, total: number): LaneSummaryData | null {
  const keys = new Set(tickedPicks.flat().map((x) => paperKey(x.paper)));
  const got = [...keys].map((k) => bands[k]).filter((b): b is Band => b != null);
  if (!got.length) return null;
  const on = Math.round((got.filter((b) => b === 3).length / got.length) * total);
  const partly = Math.round((got.filter((b) => b === 2).length / got.length) * total);
  return { total, checked: got.length, on, partly, off: Math.max(0, total - on - partly) };
}

export interface DragRow { key: string; n: number; on: number; share: number; checked: number }

/** The ticked seed dragging the set off the question: under 25% on it, ≥ 20
 *  papers, ≥ 10 checked; the one with the most off-question papers wins. */
export function dragSeed(rows: DragRow[]): DragRow | null {
  return rows
    .filter((r) => r.checked >= 10 && r.share < 0.25 && r.n >= 20)
    .sort((a, b) => (b.n - b.on) - (a.n - a.on))[0] ?? null;
}

/** Dot position in percent: four zones left→right (off-topic · tangential ·
 *  relevant · on your question), jittered by the paper key so it doesn't move. */
export function dotPos(key: string, band: Band): { left: number; top: number } {
  const h = hashInt(key);
  return { left: band * 25 + 3 + (h % 1900) / 100, top: 15 + ((h >>> 11) % 71) };
}
